import {
  HttpTransportType,
  type HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  computed,
  type MaybeRef,
  onBeforeUnmount,
  onMounted,
  unref,
  watch,
} from "vue";

import { usePlatformStore } from "@/platforms/store";

const wsApiUrl = import.meta.env.VITE_WEBSOCKET_API;

export const PUBLIC_HUB = "/hubs/updates";
export const ADMIN_HUB = "/hubs/updates";

const accessTokenFactory = async () => {
  const session = await fetchAuthSession();

  return session?.tokens?.accessToken.toString() ?? "";
};

export function useWebSocketsPublicHub(): HubConnection {
  const platformStore = usePlatformStore();

  return new HubConnectionBuilder()
    .withUrl(
      `${wsApiUrl}${PUBLIC_HUB}?platform_id=${platformStore.platformKey}`,
      {
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: accessTokenFactory,
      },
    )
    .configureLogging(LogLevel.Information)
    .withAutomaticReconnect()
    .build();
}

export function useWebSocketsAdminHub(): HubConnection {
  const platformStore = usePlatformStore();

  return new HubConnectionBuilder()
    .withUrl(
      `${wsApiUrl}${ADMIN_HUB}?platform_id=${platformStore.platformKey}`,
      {
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: accessTokenFactory,
      },
    )
    .configureLogging(LogLevel.Information)
    .withAutomaticReconnect()
    .build();
}

export function connectWebSocketsAdminHub<T>(
  channel: string,
  handler: (event: T) => unknown,
  onConnectError: (error: unknown) => void,
  isActive?: MaybeRef<boolean>,
) {
  try {
    const connection = useWebSocketsAdminHub();

    connection.on(channel, handler);

    const startConnection = async () => {
      try {
        await connection.start();
      } catch (error) {
        onConnectError(error);
      }
    };

    const shouldConnect = computed(() => {
      return isActive === undefined ? true : unref(isActive);
    });

    onMounted(() => {
      if (shouldConnect.value) {
        startConnection();
      }
    });

    watch(shouldConnect, (newValue) => {
      if (newValue) {
        startConnection();
      } else {
        connection.stop();
      }
    });

    onBeforeUnmount(() => {
      connection.stop();
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
}
